import React from 'react';
import { Box, Badge, Typography, Tooltip } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { RiskPoolOrganization } from '../../_types/risk_pool';
import { error, primary, success, warning } from '_lex/_foundations/phxColors';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams, GridColumnHeaderParams } from '@mui/x-data-grid-pro';
import { compliancePerformanceColors } from '..';
import { PhxBadge } from '_lex/_atoms/PhxBadge';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';

type RiskPoolOrganizationsProps = {
  organizations: RiskPoolOrganization[];
  isLoading?: boolean;
  query: {
    isLoading?: boolean;
    error?: any;
    data?: any;
    isFetchingNextPage?: boolean;
    hasNextPage?: boolean;
    fetchNextPage?: () => Promise<any>;
    isError?: boolean;
  };
  totalCount?: number;
  startDate?: string;
  endDate?: string;
};

const getTrendBadgeColor = (trend?: RiskPoolOrganization['trend']): 'success' | 'warning' | 'default' | 'error' => {
  if (!trend) return 'default';
  switch (trend) {
    case 'UP':
    case 'RISING':
      return 'success';
    case 'NEUTRAL':
      return 'default';
    case 'DECLINING':
      return 'warning';
    case 'DOWN':
      return 'error';
  }
};

const getTrendIcon = (trend?: RiskPoolOrganization['trend']) => {
  if (!trend) return null;
  switch (trend) {
    case 'UP':
      return <TrendingUpOutlinedIcon />;
    case 'RISING':
      return <ArrowUpwardOutlinedIcon />;
    case 'DECLINING':
      return <ArrowDownwardOutlinedIcon />;
    case 'DOWN':
      return <TrendingDownOutlinedIcon />;
    default:
      return null;
  }
};

const getComplianceColor = (compliance: number) => {
  if (compliance >= 90) return success[800];
  if (compliance >= 50) return warning[800];
  return error[800];
};

const renderHeaderWithTooltip = (headerText: string, tooltipText: string) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title={tooltipText} arrow placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
      </Tooltip>
      <Typography variant="bodyXs" sx={{ fontWeight: 600 }}>
        {headerText}
      </Typography>
    </Box>
  );
};

export default function RiskPoolOrganizations({
  organizations,
  isLoading,
  query,
  totalCount,
  startDate,
  endDate,
}: RiskPoolOrganizationsProps) {
  const navigate = useNavigate();
  const columns: ColumnDef = {
    name: {
      header: t('dashboard.riskPool.grid.organization'),
      flex: 2,
      display: 'flex',
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (!row) return null;
        return <Typography sx={{ cursor: 'pointer', color: primary[800], fontWeight: 600 }}>{row.name}</Typography>;
      },
      onClick: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (row && row.id) {
          const queryParams = new URLSearchParams();
          if (startDate) queryParams.set('startDate', startDate);
          if (endDate) queryParams.set('endDate', endDate);
          const queryString = queryParams.toString();
          const url = `/admin/dashboard/risk_pool/${row.id}${queryString ? `?${queryString}` : ''}`;
          navigate(url);
        }
      },
    },
    trend: {
      header: t('dashboard.riskPool.grid.trend'),
      flex: 1,
      display: 'flex',
      columnProps: {
        renderHeader: () => renderHeaderWithTooltip(
          t('dashboard.riskPool.grid.trend'),
          t('dashboard.riskPool.grid.trendTooltip')
        ),
      },
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (!row || !row.trend) return <Typography>--</Typography>;
        return (
          <PhxBadge
            color={getTrendBadgeColor(row.trend)}
            size='sm'
            startIcon={getTrendIcon(row.trend)}
          >
            {t(`dashboard.riskPool.trend.${row.trend}`)}
          </PhxBadge>
        );
      },
    },
    compliance: {
      header: t('dashboard.riskPool.grid.compliance'),
      flex: 1,
      display: 'flex',
      columnProps: {
        renderHeader: () => renderHeaderWithTooltip(
          t('dashboard.riskPool.grid.compliance'),
          t('dashboard.riskPool.grid.complianceTooltip')
        ),
      },
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (!row || row.compliance === undefined || !row.compliancePerformance) {
          return <Typography>--</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                borderRadius: 9999,
                width: 8,
                height: 8,
                backgroundColor: compliancePerformanceColors[row.compliancePerformance],
              }}
            />
            <Typography>{row.compliance}%</Typography>
          </Box>
        );
      },
    },
    modifiedPolicies: {
      header: t('dashboard.riskPool.grid.modifiedPolicyCount'),
      flex: 1,
      display: 'flex',
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (!row) return null;
        return (
          <Typography>
            {row.modifiedPolicyCount} / {row.totalPolicyCount}
          </Typography>
        );
      },
    },
    publishedPolicies: {
      header: t('dashboard.riskPool.grid.publishedPolicyCount'),
      flex: 1,
      display: 'flex',
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (!row) return null;
        return (
          <Typography>
            {row.publishedPolicyCount} / {row.totalPolicyCount}
          </Typography>
        );
      },
    },
  };

  return (
    <GridSection
      columns={columns}
      data={organizations}
      query={query}
      totalCount={totalCount}
      isLoading={isLoading}
    />
  );
}
